import angular from 'angular';
import uiBootstrap from 'angular-ui-bootstrap';
import ngFileUpload from 'ng-file-upload';
import csmRemoting from '../../remoting/remoting';
import csmUIUserImage from '../userImage/userImage';
import csmUIAlert from '../alert/alert';
import './ImageLoaderPanel.css';
import imageLoaderPanelTemplate from './ImageLoaderPanel.template.html';

export default angular
  .module('csm.ui.ImageLoaderPanel', [
    uiBootstrap,
    ngFileUpload,
    csmRemoting,
    csmUIUserImage,
    csmUIAlert
  ])

  /*******************************************************************************
   * $Id: ImageLoaderPanel.js 19720 2020-01-10 21:52:27Z sthames $ */
  /**
   * @ngdoc    service
   * @module   csm.ui
   * @name     csm.ui.ImageLoaderPanel.service:ImageLoaderPanel
   * @requires ui.bootstrap
   * @requires ngFileUpload
   * @requires csm.remoting.Session
   * @requires csm.ui.alert
   * @requires csm.ui.userImage
   *
   * @description
   *   A modal dialog for uploading user-supplied images. Typically, this is used
   *   for users to supply pictures of individuals, items for sale, and banners or
   *   pictures used to decorate an online store. The process works as follows:
   *
   *   1. This service is given the **URL of a current image**, the **dimensions of
   *      the box** if the image will be displayed on a web page, and the **URL of
   *      a service** to which an image file will be uploaded from the client.
   *
   *   2. The dialog opens with the image displayed in the middle and resized to be
   *      as large as possible, within the dimensions given, while maintaining the
   *      aspect ratio. If the image fails to load, the picture of a grayed-out
   *      camera is shown instead, indicating the image does not exist.
   *
   *   3. The client clicks the upload button and selects an image file, or
   *      drags and drops a file onto the image shown in the dialog.
   *
   *   4. The image file is uploaded to the handler where it is saved to a
   *      temporary URL which is passed back to the dialog. The image is then
   *      loaded from the temporary URL.
   *
   *   5. The client clicks the save button which sends a command to the handler to
   *      replace the current image with the new image uploaded by the client.
   *******************************************************************************/
  /* @ngInject */
  .service('ImageLoaderPanel', function ($q, $uibModal, Session, Alert) {
    /*****************************************************************************
     * show */
    /**
     * @ngdoc    method
     * @name     show
     * @methodOf csm.ui.ImageLoaderPanel.service:ImageLoaderPanel
     * @description
     * Shows the ImageLoaderPanel.
     *
     * @param {string} serviceURL
     *   Service handler URL. File uploads and all commands will be sent to this
     *   handler. The request parameter **`cmd`** contains the command to process:
     *
     *   - **`FILE`** - The request stream contains an image from the client. Save
     *     the image to a temporary file, set the `text/plain` response payload to
     *     the URL of the image file, and return "**Ok**" (**200**) status.
     *
     *     Return "**Bad Request**" (**410**) status for a failure that should be
     *     retried. An error alert will be displayed but the dialog will not close.
     *
     *   - **`SAVE`** - Replace the current image supplied to the service with the
     *     new, uploaded, temporary image.
     *
     *   - **`DISCARD`** - Drop the new, uploaded, temporary image.
     *
     *   - **`DROP`**    - Drop the current image supplied to the service.
     *
     * @param {string}    imageURL
     *  Current image URL as used by the website. This image will be shown in the
     *  dialog except when a temporary image has been uploaded.
     *
     * @param {object}    boxDim
     *  Object containing **`width`** and **`height`** properties describing the box
     *  in which the image will display on a web page. The dialog will be sized to
     *  accomodate these dimensions, within the limits of the window dimensions, and
     *  the image aspect ratio will be maintained.
     *
     * @param {string=}   title
     *  Dialog title. Default="Image Loader"
     *
     * @param {object=}   formData
     *  Object containing Request Parameters to send to the `serviceURL` handler with
     *  every request. This property is optional and has no effect on the dialog but
     *  all properties in this object will be sent with each request.
     *
     * @param {function=} listener
     *  Function called whenever a change is made to the target image. This can be used
     *  to reload the image after it has been updated. Function is passed the new URL or
     *  `null` if the existing image has been dropped.
     *
     * @returns {Promise}
     *  `result` property from return value of [Bootstrap's $uidModal.open() method](https://angular-ui.github.io/bootstrap/#modal).
     *  - If the dialog closes normally, promise is resolved.
     *  - If an error occurs, promise is rejected and `reason` passed is the
     *    [response from the service call that failed](https://docs.angularjs.org/api/ng/service/$http#general-usage).
     *****************************************************************************/
    this.show = function (
      serviceURL,
      imageURL,
      boxDim,
      title,
      formData,
      listener
    ) {
      /*---------------------------------------------------------------*/
      /* This tool uses ngFileUpload which requires the flash player   */
      /* for IE9. If the flash player isn't available, alert the user  */
      /* and return a promise that will not resolve. This logic is     */
      /* what the ngFileUpload module uses to determine functionality. */
      /*---------------------------------------------------------------*/
      if (
        window.FileAPI &&
        window.FileAPI.ngfFixIE &&
        !window.FileAPI.hasFlash
      ) {
        Alert.info(
          'This operation is not supported in your current browser version.',
          ' ',
          true
        );
        return $q.defer().promise;
      }

      var props = {
        template: imageLoaderPanelTemplate,
        windowClass: 'image-loader-popup',
        backdrop: 'static',
        controller: 'imageLoaderController',
        resolve: {
          parms: function () {
            return {
              serviceURL: Session.getSessionURL(serviceURL),
              imageURL: imageURL,
              boxDim: boxDim,
              title: title,
              formData: formData,
              listener: listener
            };
          }
        }
      };

      return $uibModal.open(props).result;
    };
  })

  /*******************************************************************************
   * Controller: imageLoaderController */
  /**
   * Process the organizations available and provide selection management with the
   * arrow keys.
   *
   * @param orgs  array holding a list of org objects
   *******************************************************************************/
  /* @ngInject */
  .controller(
    'imageLoaderController',
    function (
      $http,
      $q,
      $scope,
      $uibModalInstance,
      Upload,
      Alert,
      ImageLoaderPanel,
      parms
    ) {
      //  $scope.fileUploading = true;
      //  $scope.uploadProgress = 50;
      //  $scope.panelBusy = true;

      /*******************************************************************************
       * Constants
       *******************************************************************************/
      /*----------------------------*/
      /* Upload commands to server. */
      /*----------------------------*/
      var CMD_LOAD_FILE = 'FILE'; /* Upload new image file. */
      var CMD_SAVE_FILE = 'SAVE'; /* Replace current image with new image. */
      var CMD_DISCARD_FILE = 'DISCARD'; /* Discard new image file. */
      var CMD_DROP_IMAGE = 'DROP'; /* Drop current image. */

      /*----------------------*/
      /* Request field names. */
      /*----------------------*/
      var FLD_COMMAND = 'cmd'; /* Upload command to server. */

      var WINDOW_MARGIN = 20; /* Minimum dialog offset from window edges. */

      /* New, temporary image loaded flag. Set when image loaded successfully. */
      var newImageLoaded = false;

      /* Current image loaded flag. Set when image loaded successfully. */
      var oldImageLoaded = false;

      /*****************************************************************************
       * Methods
       *****************************************************************************/
      /*---------------------------------*/
      /* Sends a command to the service. */
      /*---------------------------------*/
      var callService = function (cmd) {
        return $http.get(parms.serviceURL, { params: makeCommand(cmd) });
      };

      /*------------------------------------------------*/
      /* Sends a command to the service and reloads the */
      /* current image. Returns promise with response   */
      /* `data` property after image load completes.    */
      /*------------------------------------------------*/
      var changeImage = function (cmd) {
        return doBusyWork(function () {
          return callService(cmd).then(function (r) {
            return showOldImage().then(function () {
              return $q.resolve((r || {}).data);
            });
          }, onServerError);
        });
      };

      /*----------------------------------------------------*/
      /* Calls a function in the busy state. Sets the busy  */
      /* flag, calls `work`, and clears the busy flag.      */
      /* Returns promise with the data from `work` promise. */
      /*----------------------------------------------------*/
      var doBusyWork = function (work) {
        $scope.panelBusy = true;
        return work().then(function (data) {
          $scope.panelBusy = false;
          return $q.resolve(data);
        });
      };

      /*---------------------------------------------------*/
      /* Adds upload 'cmd' to 'parms.formData' for passing */
      /* to server in GET request or as uploader formData. */
      /*---------------------------------------------------*/
      var makeCommand = function (cmd) {
        var o = {};
        o[FLD_COMMAND] = cmd;
        return angular.extend({}, parms.formData, null, o);
      };

      /*----------------------------------------------------------------*/
      /* Calls the supplied listener the target image has been changed. */
      /*----------------------------------------------------------------*/
      var notifyChanged = function (newURL) {
        if (parms.listener) parms.listener(newURL);
      };

      /*--------------------------------------------------------------*/
      /* Dismisses the dialog in the event of a server error.         */
      /* Returns false to reject the promise if called from 'then()'. */
      /*--------------------------------------------------------------*/
      var onServerError = function (reason) {
        $scope.$dismiss(reason);
        return false;
      };

      /*----------------------------------------------------------*/
      /* Loads the image from 'url' and calls                     */
      /* 'onChangeHandler(loaded)' with result. Returns a         */
      /* promise that will resolve when the image load completes. */
      /*----------------------------------------------------------*/
      var showImage = function (url, onChangeHandler) {
        return $q(function (resolve) {
          $scope.imgSrc = url ? url + '?' + new Date().getTime() : null;
          $scope.imgSrcChanged = function (loaded) {
            onChangeHandler(loaded);
            resolve();
          };
        });
      };

      /*---------------------------------------------*/
      /* Loads the current image from the server and */
      /* sets current image loaded flag from result. */
      /*---------------------------------------------*/
      var showOldImage = function () {
        return showImage(parms.imageURL, function (loaded) {
          oldImageLoaded = loaded;
        });
      };

      /*****************************************************************************
       * Scope Properties & Methods
       *****************************************************************************/
      $scope.title = parms.title || 'Image Loader';

      /*------------------*/
      /* Button enablers. */
      /*------------------*/
      $scope.showCancelBtn = function () {
        return newImageLoaded;
      };
      $scope.showUploadBtn = function () {
        return !newImageLoaded;
      };
      $scope.showRemoveBtn = function () {
        return !newImageLoaded && oldImageLoaded;
      };
      $scope.showSaveBtn = function () {
        return newImageLoaded;
      };

      $scope.cancelNewImage = function () {
        changeImage(CMD_DISCARD_FILE).then(function () {
          newImageLoaded = false;
        });
      };
      $scope.closeDialog = function () {
        $scope.$close();
      };

      /*-------------------------*/
      /* Drop the current image. */
      /*-------------------------*/
      $scope.removeOldImage = function () {
        Alert.confirm(
          'Are you sure you want to Remove the Current Image?',
          'Remove Image'
        ).then(function () {
          changeImage(CMD_DROP_IMAGE).then(function () {
            notifyChanged(null);
          });
        });
      };

      /*-----------------------------------------------------------*/
      /* Save the new image. Show a confirm dialog if replacing an */
      /* existing image. Close the dialog with the new image URL.  */
      /*-----------------------------------------------------------*/
      $scope.saveNewImage = function () {
        var confirm = oldImageLoaded
          ? Alert.confirm(
              'Are you sure you want to Replace the Current Image?',
              'Save New Image'
            )
          : $q.resolve();
        confirm.then(function () {
          changeImage(CMD_SAVE_FILE).then(function (imageURL) {
            newImageLoaded = false;
            notifyChanged(imageURL);
            $scope.$close();
          });
        });
      };

      /*----------------------*/
      /* File upload handler. */
      /*----------------------*/
      $scope.uploadFiles = function (file, errFiles) {
        /*----------------------------------------------------*/
        /* If error, file did not pass the pattern validator. */
        /*----------------------------------------------------*/
        if (errFiles && errFiles[0])
          Alert.error(
            'Selected file does not contain an image.',
            'Not Image File',
            false
          );
        /*-----------------------------------------------------------*/
        /* Upload the new file to the server. Temporary image URL    */
        /* is returned in response and new image is shown in dialog. */
        /*-----------------------------------------------------------*/ else if (
          file
        ) {
          $scope.uploadProgress = 0;
          $scope.fileUploading = true;

          Upload.upload({
            url: parms.serviceURL,
            data: { file: file },
            params: makeCommand(CMD_LOAD_FILE)
          }).then(
            /*------------------------------*/
            /* Success! Show the new image. */
            /*------------------------------*/
            function (r) {
              showImage(r.data, function (loaded) {
                newImageLoaded = loaded;
                $scope.fileUploading = false;
              });
            },

            /*--------------------------------------------------*/
            /* Fail! For a Bad Request (410) status, show the   */
            /* error message and let the user retry. For all    */
            /* others, kill the dialog and set the caller know. */
            /*--------------------------------------------------*/
            function (r) {
              $scope.fileUploading = false;
              if (r.status == 410)
                Alert.error('Please try again.', 'Image Failed to Load', false);
              else onServerError(r);
            },

            /*------------------------------------*/
            /* Progress. Update the progress bar. */
            /*------------------------------------*/
            function (e) {
              $scope.uploadProgress = Math.min(
                100,
                parseInt((100.0 * e.loaded) / e.total)
              );
            }
          );
        }
      };

      /*****************************************************************************
       * Scope Events
       *****************************************************************************/
      $scope.$on('modal.closing', function (event, reason, closed) {
        /*-------------------*/
        /* Dialog dismissed? */
        /*-------------------*/
        if (!closed) {
          /*---------------------------------*/
          /* For ESC key, do a normal close. */
          /*---------------------------------*/
          if (reason == 'escape key press') {
            event.preventDefault();
            $scope.$close();
          } else if (newImageLoaded) callService(CMD_DISCARD_FILE);
          /*--------------------------------------------------------*/
          /* Tell the service to dump any uploaded temporary image. */
          /*--------------------------------------------------------*/
        }
      });

      /*****************************************************************************
       * $uibModalInstance
       *****************************************************************************/
      /*------------------------------------------------------------*/
      /* After dialog is rendered, set the size of the image DIV to */
      /* the target dimensions. Limit to the max window dimensions. */
      /*------------------------------------------------------------*/
      $uibModalInstance.rendered.then(function () {
        var panel = document.querySelector('.image-loader-popup .modal-dialog');
        var head = panel.querySelector('.modal-header');
        var foot = panel.querySelector('.modal-footer');

        var winW = Math.max(
          document.documentElement.clientWidth,
          window.innerWidth || 0
        );
        var winH = Math.max(
          document.documentElement.clientHeight,
          window.innerHeight || 0
        );
        var maxH =
          winH - WINDOW_MARGIN * 2 - head.clientHeight - foot.clientHeight;
        var maxW = winW - WINDOW_MARGIN * 2;

        /*-------------------------------------*/
        /* Set the target dimensions. Scale to */
        /* max window dimensions if necessary. */
        /*-------------------------------------*/
        $scope.imgW = parms.boxDim.width;
        $scope.imgH = parms.boxDim.height;

        if ($scope.imgW > maxW || $scope.imgH > maxH) {
          var scaling = 1.0 / Math.max($scope.imgW / maxW, $scope.imgH / maxH);
          $scope.imgW *= scaling;
          $scope.imgH *= scaling;
        }

        /*-------------------------*/
        /* Load the current image. */
        /*-------------------------*/
        doBusyWork(showOldImage);
      });
    }
  ).name;
